import { readArray, readEnum, readMoney, readNumber, readOptionalString, readRecord, readString } from '../readers';
import { semanticGet, semanticPost } from '../semanticRepository';
import type { PayoutStatus } from '../payouts/model';
import type { AffiliateOverview, AffiliatePayout, AffiliatePayoutMethod } from './model';

const METHODS: readonly AffiliatePayoutMethod[] = ['cash', 'credit'];
const STATUSES: readonly PayoutStatus[] = ['review', 'pending', 'paid', 'failed', 'declined'];

const readPayout = (value: unknown): AffiliatePayout => {
  const row = readRecord(value, 'affiliate payout');
  return {
    id: readString(row.id, 'affiliate payout id'),
    method: readEnum(row.method, METHODS, 'affiliate payout method'),
    amountUsd: readMoney(row.amountUsd, 'affiliate payout amount'),
    feeUsd: readMoney(row.feeUsd, 'affiliate payout fee'),
    sentUsd: readMoney(row.sentUsd, 'affiliate payout sent amount'),
    status: readEnum(row.status, STATUSES, 'affiliate payout status'),
    declineReason: readOptionalString(row.declineReason, 'affiliate payout decline reason'),
    createdAt: readString(row.createdAt, 'affiliate payout date'),
  };
};

/**
 * Every successful response describes a real affiliate: the gateway enrols the
 * caller on their first read of the console, so there is no unenrolled shape to
 * tolerate and every field below is required.
 */
export const readAffiliateOverview = (value: unknown): AffiliateOverview => {
  const row = readRecord(value, 'affiliate overview');
  return {
    code: readString(row.code, 'affiliate code'),
    payoutMethod: readEnum(row.payoutMethod, METHODS, 'affiliate payout method'),
    pendingUsd: readMoney(row.pendingUsd, 'affiliate pending USD'),
    availableUsd: readMoney(row.availableUsd, 'affiliate available USD'),
    paidUsd: readMoney(row.paidUsd, 'affiliate paid USD'),
    clicks: readNumber(row.clicks, 'affiliate click count'),
    referrals: readNumber(row.referrals, 'affiliate referral count'),
    earningCustomers: readNumber(row.earningCustomers, 'affiliate earning customer count'),
    payouts: readArray(row.payouts, 'affiliate payouts').map(readPayout),
    cashFeeUsd: readMoney(row.cashFeeUsd, 'affiliate cash fee'),
    cashReceiveUsd: readMoney(row.cashReceiveUsd, 'affiliate cash received amount'),
  };
};

export const fetchAffiliateOverview = (): Promise<AffiliateOverview> =>
  semanticGet('/v1/affiliate', readAffiliateOverview);

/**
 * Tells the gateway that this visit followed an affiliate link.
 *
 * Unauthenticated on purpose: the whole point is the person who has no account
 * yet. It answers 204 for a code nobody owns exactly as it does for a real one,
 * so nothing can be learned from the response and there is nothing to read.
 *
 * The rejection is swallowed rather than surfaced. This runs beside somebody
 * reading a marketing page and its only product is a tally on somebody else's
 * console; a failed tally must never reach the reader as an error.
 */
export const recordAffiliateClick = (code: string): void => {
  void semanticPost('/public/affiliate/click', { code }, () => undefined).catch(() => undefined);
};

export const setAffiliatePayoutMethod = (method: AffiliatePayoutMethod): Promise<AffiliateOverview> =>
  semanticPost('/v1/affiliate/payout-method', { method }, readAffiliateOverview);

export const withdrawAffiliateBalance = (): Promise<AffiliatePayout> =>
  semanticPost('/v1/affiliate/withdraw', {}, readPayout);
